import { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { UsersAPI } from '../api'
import { Search, Edit, Trash2, ShieldCheck, Dock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAdmin } from '../lib/hooks'
import { Pagination } from './Pagination'

const AdminUsers = () => {
  const { isAdmin, loading: adminLoading } = useAdmin()
  const { user } = useUser()
  const navigate = useNavigate()

  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [editingUser, setEditingUser] = useState<any>(null)

  // Edit form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [isAdminUser, setIsAdminUser] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const res = await UsersAPI.getAll()
      setItems(res.data || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAdmin) load()
  }, [isAdmin])

  const handleDeactivate = async (clerkId: string) => {
    if (window.confirm('¿Estás seguro de que deseas desactivar este usuario?')) {
      await UsersAPI.deactivate(clerkId)
      load()
    }
  }

  const handleEdit = (user: any) => {
    setEditingUser(user)
    setFirstName(user.firstName || '')
    setLastName(user.lastName || '')
    setIsActive(user.isActive)
    setIsAdminUser(user.isAdmin || false)
  }

  const handleUpdate = async () => {
    if (editingUser) {
      // 1. Check if we are promoting to Admin
      const isPromoting = isAdminUser && !editingUser.isAdmin;

      // 2. Prepare update data (excluding isAdmin if promoting)
      const updateData: any = {
        firstName,
        lastName,
        isActive,
      };

      if (!isPromoting) {
        // If not promoting, we can update isAdmin directly
        updateData.isAdmin = isAdminUser;
      }

      // 3. Update basic info
      await UsersAPI.update(editingUser.clerkId, updateData);

      // 4. Handle Promotion Request
      if (isPromoting) {
        if (user) {
          await UsersAPI.requestAdmin(
            editingUser.clerkId,
            user.id,
            `${firstName} ${lastName}`,
            editingUser.email
          );
          alert('Se ha enviado una solicitud de aprobación para el permiso de Administrador. Recibirás un correo para confirmar.');
        } else {
          alert('Error: No se pudo identificar al usuario solicitante.');
        }
      }

      setEditingUser(null)
      load()
    }
  }

  if (adminLoading) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Cargando permisos...</p></div>
  if (!isAdmin) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Acceso restringido</p></div>

  const filtered = items.filter((it) => {
    const q = query.trim().toLowerCase()
    return q ? (
      it.email?.toLowerCase().includes(q) ||
      it.firstName?.toLowerCase().includes(q) ||
      it.lastName?.toLowerCase().includes(q)
    ) : true
  })

  const filteredTotal = filtered.length
  const startIdx = (page - 1) * pageSize
  const endIdx = Math.min(startIdx + pageSize, filteredTotal)
  const pageItems = filtered.slice(startIdx, endIdx)

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Prevent admins from editing their own account to avoid accidental self-demotion
  const isSelf = (clerkId: string) => {
    return user?.id === clerkId
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-24 mb-16">
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl md:text-3xl font-bold">Administración de Usuarios</h2>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 shadow-sm hover:bg-gray-200 transition-colors dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700"
            >
              Volver a Hostings
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1) }}
                placeholder="Buscar por email o nombre..."
                className="w-full border rounded-md px-3 py-2 pl-9 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder-gray-400"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Mostrando {filteredTotal === 0 ? 0 : startIdx + 1}-{endIdx} de {filteredTotal}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden dark:bg-slate-800 dark:border-slate-700">
        {loading ? (
          <div className="px-5 py-8 text-gray-500">Cargando usuarios...</div>
        ) : (
          <div className="px-5 py-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-slate-700/50">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Usuario</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Email</th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Estado</th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Registrado</th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
                {pageItems.map((it) => (
                  <tr key={it.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {it.imageUrl ? (
                          <img src={it.imageUrl} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500 dark:bg-slate-700 dark:text-gray-300">
                            {it.firstName?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div className="font-medium text-gray-900 flex items-center gap-1 dark:text-white">
                          {it.firstName} {it.lastName}
                          {it.isAdmin && (
                            <span title={isSelf(it.clerkId) ? 'Tu cuenta (Administrador)' : 'Administrador'}>
                              <ShieldCheck className={`w-4 h-4 ${isSelf(it.clerkId) ? 'text-amber-500' : 'text-blue-600'}`} />
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-500 dark:text-gray-400">{it.email}</div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${it.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}>
                        {it.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(it.createdAt)}</span>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => navigate(`/admin/users/${it.clerkId}/dashboard`)}
                          className="p-2 text-[#d97706] hover:bg-orange-50 rounded-full transition-colors dark:text-[#fbbf24] dark:hover:bg-orange-900/30"
                          title="Ver dashboard del usuario"
                        >
                          <Dock size={18} />
                        </button>
                        <button
                          onClick={() => handleEdit(it)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors dark:text-blue-400 dark:hover:bg-blue-900/30"
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>
                        {it.isActive && (
                          <button
                            onClick={() => handleDeactivate(it.clerkId)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors dark:text-red-400 dark:hover:bg-red-900/30"
                            title="Desactivar"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Edit Modal */}
            {editingUser && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-md dark:bg-slate-800 dark:border dark:border-slate-700">
                  <h3 className="text-lg font-semibold mb-4 dark:text-white">Editar Usuario</h3>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Nombre</label>
                      <input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Apellido</label>
                      <input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700 w-24 dark:text-gray-300">Estado Activo</label>
                      <button
                        onClick={() => !isSelf(editingUser?.clerkId) && setIsActive(!isActive)}
                        disabled={isSelf(editingUser?.clerkId)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isActive ? 'bg-blue-600' : 'bg-gray-200'
                          } ${isSelf(editingUser?.clerkId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700 w-24 dark:text-gray-300">Administrador</label>
                      <button
                        onClick={() => !isSelf(editingUser?.clerkId) && setIsAdminUser(!isAdminUser)}
                        disabled={isSelf(editingUser?.clerkId)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isAdminUser ? 'bg-blue-600' : 'bg-gray-200'
                          } ${isSelf(editingUser?.clerkId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isAdminUser ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-6">
                    <button
                      onClick={() => setEditingUser(null)}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md dark:text-gray-300 dark:hover:bg-slate-700"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleUpdate}
                      className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md dark:bg-blue-600 dark:hover:bg-blue-700"
                    >
                      Guardar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Pagination Controls */}
          </div>
        )}
        {!loading && (
          <Pagination
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={filteredTotal}
            className="bg-gray-50 dark:bg-slate-900/50 rounded-b-xl"
          />
        )}
      </div>
    </div>
  )
}

export default AdminUsers